import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, DollarSign, ArrowRight, LayoutGrid, List, Brain } from 'lucide-react';
import { formatCurrency, getStageColor } from '@/lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { key: 'concept', label: 'Concept', color: 'border-t-gray-400' },
  { key: 'feasibility', label: 'Feasibility', color: 'border-t-blue-400' },
  { key: 'development', label: 'Development', color: 'border-t-yellow-400' },
  { key: 'financial_close', label: 'Financial Close', color: 'border-t-purple-400' },
  { key: 'construction', label: 'Construction', color: 'border-t-orange-400' },
  { key: 'operational', label: 'Operational', color: 'border-t-green-400' }
];

function DealCard({ deal, onMove, navigate }) {
  const currentIdx = STAGES.findIndex(s => s.key === deal.stage);

  return (
    <div
      className="p-2.5 rounded border border-border bg-card cursor-pointer hover:shadow-sm transition-all group"
      data-testid={`pipeline-card-${deal.id}`}
    >
      <div onClick={() => navigate(`/deals/${deal.id}`)}>
        <h4 className="font-medium text-xs leading-tight mb-1 group-hover:text-accent transition-colors">{deal.name}</h4>
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1.5">
          <span className="flex items-center gap-0.5"><MapPin size={9} />{deal.country}</span>
          <span className="font-mono">{formatCurrency(deal.capital_required)}</span>
        </div>
        <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">{deal.sector}</Badge>
      </div>
      {currentIdx < STAGES.length - 1 && (
        <button
          className="w-full mt-1.5 pt-1.5 border-t border-dashed border-border text-[10px] text-muted-foreground hover:text-accent flex items-center justify-center gap-1 transition-colors"
          onClick={(e) => { e.stopPropagation(); onMove(deal.id, STAGES[currentIdx + 1].key); }}
          data-testid={`move-${deal.id}`}
        >
          <ArrowRight size={10} />{STAGES[currentIdx + 1].label}
        </button>
      )}
    </div>
  );
}

export function PipelinePage() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectorFilter, setSectorFilter] = useState('');
  const [view, setView] = useState('kanban');

  useEffect(() => { fetchDeals(); }, []);

  const fetchDeals = async () => {
    try {
      const res = await axios.get(`${API}/deals`, { headers: getAuthHeaders() });
      setDeals(res.data);
    } catch (error) {
      console.error('Failed to fetch deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const moveDeal = useCallback(async (dealId, newStage) => {
    try {
      await axios.patch(`${API}/deals/${dealId}`, { stage: newStage }, { headers: getAuthHeaders() });
      setDeals(prev => prev.map(d => d.id === dealId ? { ...d, stage: newStage } : d));
      toast.success(`Deal moved to ${newStage.replace('_', ' ')}`);
    } catch (error) {
      toast.error('Failed to move deal');
    }
  }, [getAuthHeaders]);

  const sectors = [...new Set(deals.map(d => d.sector))].sort();
  const filteredDeals = sectorFilter ? deals.filter(d => d.sector === sectorFilter) : deals;

  const getColumnDeals = (stage) => filteredDeals.filter(d => d.stage === stage);
  const totalCapital = (stage) => getColumnDeals(stage).reduce((a, d) => a + d.capital_required, 0);

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-muted-foreground">Loading pipeline...</div></div>;

  return (
    <div className="p-6 space-y-4" data-testid="pipeline-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Deal Pipeline</h1>
          <p className="text-muted-foreground">Track deal progression from concept to operational</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={sectorFilter} onValueChange={setSectorFilter}>
            <SelectTrigger className="w-52" data-testid="pipeline-sector-filter">
              <SelectValue placeholder="All Sectors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sectors</SelectItem>
              {sectors.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex border rounded overflow-hidden">
            <Button
              size="sm"
              variant={view === 'kanban' ? 'default' : 'ghost'}
              className="rounded-none h-9"
              onClick={() => setView('kanban')}
              data-testid="view-kanban"
            >
              <LayoutGrid size={14} />
            </Button>
            <Button
              size="sm"
              variant={view === 'list' ? 'default' : 'ghost'}
              className="rounded-none h-9"
              onClick={() => setView('list')}
              data-testid="view-list"
            >
              <List size={14} />
            </Button>
          </div>
        </div>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-6 gap-2">
        {STAGES.map(stage => {
          const count = getColumnDeals(stage.key).length;
          const capital = totalCapital(stage.key);
          return (
            <div key={stage.key} className={`px-3 py-2 rounded border-t-2 bg-card border border-border ${stage.color}`} data-testid={`stage-summary-${stage.key}`}>
              <p className="text-[10px] text-muted-foreground leading-none mb-0.5">{stage.label}</p>
              <div className="flex items-baseline gap-1.5">
                <span className="text-lg font-bold font-mono leading-none">{count}</span>
                <span className="text-[10px] text-muted-foreground font-mono">{formatCurrency(capital)}</span>
              </div>
            </div>
          );
        })}
      </div>

      {view === 'kanban' ? (
        <div className="grid grid-cols-6 gap-2 min-h-[500px]">
          {STAGES.map(stage => (
            <div key={stage.key} className="space-y-1.5" data-testid={`column-${stage.key}`}>
              <div className={`px-2 py-1.5 rounded-t border-t-2 bg-muted/40 ${stage.color}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{stage.label}</span>
                  <span className="text-[10px] font-mono bg-background px-1 py-0.5 rounded leading-none">{getColumnDeals(stage.key).length}</span>
                </div>
              </div>
              <div className="space-y-1.5 min-h-[200px]">
                {getColumnDeals(stage.key).map(deal => (
                  <DealCard key={deal.id} deal={deal} onMove={moveDeal} navigate={navigate} />
                ))}
                {getColumnDeals(stage.key).length === 0 && (
                  <div className="text-center py-6 text-[10px] text-muted-foreground border border-dashed rounded">No deals</div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Deal</th>
                  <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Sector</th>
                  <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Country</th>
                  <th className="text-right p-3 text-xs font-semibold text-muted-foreground">Capital</th>
                  <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Stage</th>
                  <th className="text-right p-3 text-xs font-semibold text-muted-foreground">Action</th>
                </tr>
              </thead>
              <tbody>
                {STAGES.map(stage => getColumnDeals(stage.key).map(deal => {
                  const currentIdx = STAGES.findIndex(s => s.key === deal.stage);
                  return (
                    <tr key={deal.id} className="border-b hover:bg-muted/50 cursor-pointer" onClick={() => navigate(`/deals/${deal.id}`)} data-testid={`list-row-${deal.id}`}>
                      <td className="p-3 text-sm font-medium">{deal.name}</td>
                      <td className="p-3"><Badge variant="outline" className="text-[10px]">{deal.sector}</Badge></td>
                      <td className="p-3 text-sm text-muted-foreground">{deal.country}</td>
                      <td className="p-3 text-sm font-mono text-right">{formatCurrency(deal.capital_required)}</td>
                      <td className="p-3"><Badge className={`text-[10px] capitalize ${getStageColor(deal.stage)}`}>{deal.stage.replace('_', ' ')}</Badge></td>
                      <td className="p-3 text-right">
                        {currentIdx < STAGES.length - 1 && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); moveDeal(deal.id, STAGES[currentIdx + 1].key); }} data-testid={`list-move-${deal.id}`}>
                            <ArrowRight size={12} className="mr-1" />{STAGES[currentIdx + 1].label}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                }))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
